import React from "react"
import {
  Accordion,
  makeStyles,
  Theme,
  AccordionSummary,
  Box,
  AccordionDetails,
} from "@material-ui/core"
import { SchemaFieldVariant } from "./schema"
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from "@material-ui/icons"
import { Subtitle } from "@citydna/common"
import { ChangeFieldType } from "./ChangeFieldType"
import { useCommonFieldProps } from "./CommonFieldPropsContext"
import { useFormContext, useWatch } from "react-hook-form"

/** Scoped @material-ui/core stylesheet */
const useStyles = makeStyles<Theme, { hasName: boolean }>(theme => ({
  root: {
    boxShadow: "none",
  },
  summary: {
    "&.Mui-expanded": {
      borderBottom: `1px solid ${
        theme.palette.type === "dark"
          ? theme.palette.grey[400]
          : theme.palette.grey[200]
      }`,
    },
    "& .MuiAccordionSummary-content.Mui-expanded": {
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
    },
  },
  dragIndicator: {
    cursor: "grab",
    margin: theme.spacing(1),
    color:
      theme.palette.type === "dark"
        ? theme.palette.grey[400]
        : theme.palette.grey[200],
  },
  title: {
    "& .MuiTypography-h2": {
      color: ({ hasName }) =>
        hasName
          ? "inherit"
          : theme.palette.type === "dark"
          ? theme.palette.grey[400]
          : theme.palette.grey[200],
    },
  },
  details: {
    flexDirection: "column",
  },
}))

export const SchemaFieldAccordion: React.FC = ({ children }) => {
  /** Get common schema field props */
  const { field, index } = useCommonFieldProps()
  const { control } = useFormContext()
  const fieldAlias = useWatch({
    control,
    name: `fields[${index}].alias`,
    defaultValue:
      field.alias ||
      `Unnamed ${fieldVariantNiceName(field.variant)?.toLowerCase()}`,
  })

  /** Get scoped CSS classes */
  const classes = useStyles({ hasName: Boolean(fieldAlias) })

  return (
    <Accordion className={classes.root} defaultExpanded>
      <AccordionSummary
        className={classes.summary}
        expandIcon={<ExpandMoreRoundedIcon />}
      >
        <Box display="flex" alignItems="center">
          <Box flexGrow={1}>
            <Subtitle
              primary={fieldAlias}
              secondary={<ChangeFieldType />}
              className={classes.title}
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <Box pt={1}>{children}</Box>
      </AccordionDetails>
    </Accordion>
  )
}

const fieldVariantNiceName = (variant: SchemaFieldVariant) => {
  switch (variant) {
    case "text":
      return "Text field"
    case "switch":
      return "True/false field"
    case "imageUpload":
      return "Image upload field"
    case "select":
      return "Dropdown select field"
  }
}
